import { Button } from "@/components/ui/button";
import { Search, User, Heart, ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link, NavLink, useNavigate } from "react-router-dom";
import LanguageSelector from "@/components/LanguageSelector";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";

const Header = () => {
  const { user, signOut, loading } = useAuth();
  const { t } = useLanguage();
  const { totalItems } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navLinks = [
    { to: "/", label: t.nav.home || 'Главна', end: true },
    { to: "/properties", label: t.nav.deals },
    { to: "/services", label: "Услуги" },
    { to: "/contact", label: t.footer.contact.title },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl">🐊</span>
          <span className="text-xl font-bold text-primary">Dealigator</span>
        </Link>
        
        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? 'font-bold text-primary' : 'font-medium text-muted-foreground hover:text-foreground'}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        
        <div className="flex items-center gap-2 md:gap-3">
          <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={() => navigate('/properties')} aria-label="Search properties">
            <Search className="h-5 w-5" />
          </Button>

          {user && (
            <Button variant="ghost" size="icon" className="hidden md:inline-flex" onClick={() => navigate('/favorites')} aria-label="Favorites">
              <Heart className="h-5 w-5" />
            </Button>
          )}

          <Button variant="ghost" size="icon" className="relative" onClick={() => navigate('/cart')} aria-label="Shopping cart">
            <ShoppingCart className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs rounded-full bg-accent text-accent-foreground">
                {totalItems}
              </span>
            )}
          </Button>
          
          <LanguageSelector />
          
          {/* Desktop auth buttons */}
          <div className="hidden md:flex items-center gap-2">
            {loading ? (
              <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {user.email?.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=filters')}>
                    {t.nav.myFilters}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=alerts')}>
                    {t.nav.myAlerts}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=orders')}>
                    Моите поръчки
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    {t.nav.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/auth')}>
                  {t.nav.signIn}
                </Button>
                <Button size="sm" onClick={() => navigate('/auth')}>
                  {t.nav.getStarted}
                </Button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[280px] p-0">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2">
              <span className="text-2xl">🐊</span>
              <span className="text-lg font-bold text-primary">Dealigator</span>
            </SheetTitle>
          </SheetHeader>

          <nav className="flex flex-col p-4 gap-1">
            {navLinks.map((link) => (
              <SheetClose asChild key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'font-bold text-primary bg-primary/5'
                        : 'font-medium text-foreground hover:bg-muted'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </SheetClose>
            ))}
          </nav>

          <div className="border-t border-border p-4 space-y-2">
            {user && (
              <SheetClose asChild>
                <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => navigate('/favorites')}>
                  <Heart className="h-4 w-4" />
                  {t.nav.favorites || 'Любими'}
                </Button>
              </SheetClose>
            )}
            <SheetClose asChild>
              <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => navigate('/properties')}>
                <Search className="h-4 w-4" />
                {t.nav.deals}
              </Button>
            </SheetClose>
          </div>

          <div className="border-t border-border p-4 space-y-2">
            {loading ? (
              <div className="h-9 bg-muted animate-pulse rounded-md" />
            ) : user ? (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive"
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                >
                  {t.nav.signOut}
                </Button>
              </>
            ) : (
              <>
                <SheetClose asChild>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                    {t.nav.signIn}
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button className="w-full" onClick={() => navigate('/auth')}>
                    {t.nav.getStarted}
                  </Button>
                </SheetClose>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
